import React, { useEffect, useState, useRef } from 'react'
import { compose } from 'redux'
import { connect } from 'react-redux'
import * as Sentry from '@sentry/browser'
import firebase from 'firebase/app'
// Required for side-effects
import 'firebase/firestore'
import { Icon } from 'antd'
import { withRouter } from 'react-router-dom'
import styled from 'styled-components'
import moment from 'moment'
import { isEqual, omit } from 'lodash'
import { white, red } from '@edulastic/colors'
import useInterval from '@use-it/interval'
import { db } from '@edulastic/common/src/Firebase'
import { notification } from '@edulastic/common'
import { roleuser } from '@edulastic/constants'
import { testActivityApi } from '@edulastic/api'
import AssignmentTimeEndedAlert from './AssignmentTimeEndedAlert'
import {
  clearTimedTestHandoffState,
  clearTimeEndedAlertOpen,
  clearSectionTimerHandoff,
  isSectionTimerHandoff,
  isTimeEndedAlertOpen,
  markSectionTimerHandoff,
  registerTimedTestTimeSpentFlush,
  setTimeEndedAlertOpen,
  wasAutoSubmitTriggered,
} from './timedTestTimerHandoff'
import { utaStartTimeUpdateRequired } from '../../../student/sharedDucks/AssignmentModule/ducks'
import { getUserRole } from '../../../author/src/selectors/user'
import { isSectionAdaptiveTestSelector } from '../../selectors/test'

export const TIME_UPDATE_TYPE = {
  START: 'start',
  RESUME: 'resume',
}

const UTA_STATUS = {
  ACTIVE: 'active',
  PAUSED: 'paused',
}

const TIMER_INTERVAL = 1000
const SYNC_INTERVAL = 30000
const CAUTION_TIME = 120000

const isEnteringSectionFromHub = (history) =>
  history.location?.state?.enteringSectionFromHub === true

const isStaleSectionExpiryAlertOnItemEntry = (history, utaId) => {
  if (!isTimeEndedAlertOpen(utaId) || !wasAutoSubmitTriggered(utaId)) {
    return false
  }
  return isEnteringSectionFromHub(history)
}

const getFormattedTime = (currentAssignmentTime) => {
  const duration = moment.duration(currentAssignmentTime)
  const h = duration.hours()
  const m = duration.minutes()
  const s = duration.seconds()
  const time = `${h > 9 ? h : `0${h}`} : ${m > 9 ? m : `0${m}`} : ${
    s > 9 ? s : `0${s}`
  }`
  return time
}

const firestoreCollectionName = 'timedAssignmentUTAs'

function handlePaused(history) {
  notification({
    msg: 'Assignment is Paused , Please check with your instructor.',
    zIndex: 10000,
  })
  history.push('/home/assignments')
}

const TimedTestTimer = ({
  utaId,
  history,
  groupId,
  fgColor,
  bgColor = 'transparent',
  updateUtaTimeType = null,
  resetUpdateUtaType,
  isPasswordValidated,
  userRole,
  isPreview,
  allowedTime,
  style = {},
  isSectionAdaptiveTest = false,
}) => {
  const [uta, setUtaDoc] = useState()
  const [upstreamUta, setUpstreamUta] = useState()
  const [autoSubmitPopUp, setAutoSubmitpopUp] = useState(() => {
    if (isStaleSectionExpiryAlertOnItemEntry(history, utaId)) {
      clearTimedTestHandoffState(utaId)
      return false
    }
    if (isEnteringSectionFromHub(history)) {
      clearTimedTestHandoffState(utaId)
    }
    return isTimeEndedAlertOpen(utaId)
  })
  const [currentAssignmentTime, setCurrentAssignmentTime] = useState(null)
  const docRef = useRef(
    db.collection(firestoreCollectionName).doc(utaId || 'NONEXISTENT')
  )
  const utaRef = useRef(upstreamUta)
  const currentAssignmentTimeRef = useRef(null)
  const autoSubmitPopUpRef = useRef(autoSubmitPopUp)
  const isSectionHandoffPending =
    history.location?.state?.isBaseSectionAutoSubmitted === true
  const timerFrozen = autoSubmitPopUp || isSectionHandoffPending
  const isAuthorPreview = userRole !== roleuser.STUDENT && isPreview

  useEffect(() => {
    currentAssignmentTimeRef.current = currentAssignmentTime
  }, [currentAssignmentTime])

  useEffect(() => {
    autoSubmitPopUpRef.current = autoSubmitPopUp
    if (autoSubmitPopUp && utaId) {
      setTimeEndedAlertOpen(utaId, true)
    }
  }, [autoSubmitPopUp, utaId])

  useEffect(() => {
    if (isEnteringSectionFromHub(history)) {
      clearTimedTestHandoffState(utaId)
    }
    if (isStaleSectionExpiryAlertOnItemEntry(history, utaId)) {
      setAutoSubmitpopUp(false)
    }
  }, [history, utaId])

  useEffect(() => {
    if (timerFrozen && currentAssignmentTime === null && !isAuthorPreview) {
      setCurrentAssignmentTime(0)
    }
  }, [timerFrozen, currentAssignmentTime, isAuthorPreview])

  // A fresh mount always re-derives the countdown from the server doc, which is
  // exactly what the section-submit suppression was protecting. Clearing it here
  // (rather than only on `enteringSectionFromHub`) means a section entered by any
  // route - browser back/forward, a direct URL - cannot inherit a stuck flag and
  // sit with a frozen, never-expiring timer.
  useEffect(() => {
    clearSectionTimerHandoff(utaId)
  }, [utaId])

  useEffect(() => {
    let unsubscribe = () => {}
    if (!isAuthorPreview) {
      unsubscribe = db
        .collection(firestoreCollectionName)
        .doc(utaId || 'NONEXISTENT')
        .onSnapshot({ includeMetadataChanges: true }, (_doc) => {
          if (_doc.metadata.fromCache) return null
          setUpstreamUta(_doc.data())
          utaRef.current = _doc.data()
        })
    }
    if (isAuthorPreview) {
      setCurrentAssignmentTime(allowedTime)
    }
    return () => {
      unsubscribe()
    }
  }, [])

  // There is no remaining-ms session cache: Firestore is the single source of
  // truth. Adaptive pushes the countdown there before navigating (the flush
  // registered below); non-adaptive no longer remounts a review timer at all,
  // so it has nothing to hand off (ASM-716 is scoped to adaptive).
  useEffect(() => {
    /**
     * Writes the countdown back to Firestore as `timeSpent`. Three callers:
     * this effect's cleanup (navigating away), the `beforeunload` handler
     * (tab close), and - for adaptive - `flushTimedTestTimeSpent`, which the
     * item -> review/hub sagas await so the next mount does not read a stale
     * doc.
     *
     * Returns a promise so those callers can await the Firestore ack.
     * It no-ops when writing would corrupt the doc rather than update it:
     * - autoSubmitPopUpRef: the section expired and the server has already
     *   reset the doc to the next section; `currentAssignmentTime` now holds
     *   that section's full time, so writing it back would burn it.
     * - isSectionTimerHandoff: same hazard via manual section submit - the
     *   submitSection saga sets the flag precisely to suppress this write.
     * - isAuthorPreview: teacher preview has no real UTA to write to.
     * - the remaining checks are "nothing meaningful to write yet": no utaId,
     *   the countdown is not running (<= 0 or not yet derived), or the doc /
     *   subscription is not up.
     */
    function writeTimeSpentToFirestore() {
      if (
        autoSubmitPopUpRef.current ||
        isSectionTimerHandoff(utaId) ||
        isAuthorPreview ||
        !utaId ||
        !(currentAssignmentTimeRef.current > 0) ||
        !utaRef.current ||
        !docRef.current
      ) {
        return Promise.resolve()
      }
      const remaining = currentAssignmentTimeRef.current
      const { timeSpent = 0, allowedTime: allowedTestTime } = utaRef.current
      const _syncOffset = allowedTestTime - remaining || 0
      return docRef.current
        .update({
          lastResumed: firebase.firestore.FieldValue.serverTimestamp(),
          timeSpent: Math.max(timeSpent, _syncOffset),
        })
        .catch((err) => {
          Sentry.captureException(err)
        })
    }

    function updateFirestoreInfo(tabClosed) {
      try {
        if (isSectionTimerHandoff(utaId)) {
          return
        }
        const { timeSpent = 0, allowedTime: allowedTestTime } = utaRef.current
        const _syncOffset =
          allowedTestTime - currentAssignmentTimeRef.current || 0
        testActivityApi.updateFirestoreTimeSpent({
          utaId,
          payload: {
            timeSpent: Math.max(timeSpent, _syncOffset),
            tabClosed,
          },
        })
      } catch (err) {
        Sentry.captureException(err)
      }
    }

    function syncTimeSpentOnNavigateAway() {
      // After section expiry / manual section submit the server resets Firestore
      // for the next section; do not write the prior section's leftover timeSpent
      // over that reset (unmount race on review → hub).
      if (autoSubmitPopUpRef.current || isSectionTimerHandoff(utaId)) {
        return
      }
      writeTimeSpentToFirestore()
    }

    function logDataTabClose() {
      if (
        !isAuthorPreview &&
        utaId &&
        currentAssignmentTimeRef.current > 0 &&
        utaRef.current
      ) {
        updateFirestoreInfo(true)
      }
    }

    if (isSectionAdaptiveTest && !isAuthorPreview) {
      registerTimedTestTimeSpentFlush(writeTimeSpentToFirestore)
    }

    window.addEventListener('beforeunload', logDataTabClose)

    return () => {
      // In-app navigation (e.g. question view ↔ review page) must not pause the
      // timer; only an actual tab/window close should set status to paused.
      syncTimeSpentOnNavigateAway()
      window.removeEventListener('beforeunload', logDataTabClose)
      if (isSectionAdaptiveTest) {
        registerTimedTestTimeSpentFlush(null)
      }
    }
  }, [isSectionAdaptiveTest, isAuthorPreview, utaId])

  useEffect(() => {
    if (!upstreamUta || isAuthorPreview) {
      return
    }

    const isModified = !isEqual(
      omit(uta, ['lastResumed', 'timeSpent']),
      omit(upstreamUta, ['lastResumed', 'timeSpent'])
    )

    // Section-adaptive same-limit handoff: processSectionStatus resets Firestore
    // to { allowedTime: <same>, timeSpent: 0 }. `isModified` omits timeSpent (to
    // ignore routine sync ticks), so without this the prior section's leftover
    // countdown sticks on a still-mounted timer and can be written back.
    //
    // Adaptive-only: a non-adaptive timed test has one overall timer, and
    // re-deriving it on a timeSpent reset would hand the student the full time
    // again. Note this is a secondary net only - `uta` is not refreshed on
    // timeSpent-only snapshots, so `uta.timeSpent` can be a stale 0 and this
    // never fires. The authoritative suppression is markSectionTimerHandoff in
    // the submitSection saga; do not drop that on the strength of this branch.
    const isSectionTimeReset =
      isSectionAdaptiveTest &&
      !!uta &&
      (uta.timeSpent || 0) > 0 &&
      (upstreamUta.timeSpent || 0) === 0

    if (!isModified && !isSectionTimeReset) {
      return
    }

    setUtaDoc(upstreamUta)

    if (isModified) {
      const initialUtaUpdate =
        upstreamUta.status === UTA_STATUS.ACTIVE &&
        updateUtaTimeType === TIME_UPDATE_TYPE.START
      const isPasswordProtected =
        isPasswordValidated && updateUtaTimeType === TIME_UPDATE_TYPE.RESUME
      const timeStamp = firebase.firestore.FieldValue.serverTimestamp()
      if (initialUtaUpdate || isPasswordProtected) {
        let data = { startTime: timeStamp, status: UTA_STATUS.ACTIVE }
        if (updateUtaTimeType === TIME_UPDATE_TYPE.RESUME) {
          const timeStampType =
            !uta || uta?.startTime
              ? { lastResumed: timeStamp }
              : { startTime: timeStamp }
          data = { ...timeStampType, status: UTA_STATUS.ACTIVE }
        }
        resetUpdateUtaType(null)
        docRef.current.update(data)
      } else if (upstreamUta?.status === UTA_STATUS.PAUSED) {
        console.warn("This shouldn't happen. the assignment is already paused")
        handlePaused(history)
      }
    }

    if (currentAssignmentTime === null && !timerFrozen) {
      const allowedTimeMs = upstreamUta?.allowedTime
      // Always derive from Firestore (no remaining-ms cache).
      setCurrentAssignmentTime(
        allowedTimeMs - (upstreamUta?.timeSpent || 0) || 0
      )
    } else if (
      !timerFrozen &&
      upstreamUta.allowedTime &&
      uta?.allowedTime &&
      upstreamUta.allowedTime !== uta.allowedTime
    ) {
      // ASM-405: `allowedTime` changes for section transition or teacher edit.
      // Server doc is authoritative: allowedTime - timeSpent.
      if (isSectionAdaptiveTest) {
        clearTimeEndedAlertOpen(utaId)
        markSectionTimerHandoff(utaId)
      }
      setCurrentAssignmentTime(
        upstreamUta.allowedTime - (upstreamUta.timeSpent || 0) || 0
      )
    } else if (!timerFrozen && isSectionTimeReset) {
      // Same allowedTime across sections: still re-derive from server reset.
      markSectionTimerHandoff(utaId)
      setCurrentAssignmentTime(
        (upstreamUta.allowedTime || 0) - (upstreamUta.timeSpent || 0) || 0
      )
    }
  }, [upstreamUta, uta, updateUtaTimeType, isSectionAdaptiveTest])

  const timerPaused = uta?.status === UTA_STATUS.PAUSED

  useEffect(() => {
    if (timerPaused && uta?.byTeacher) {
      handlePaused(history)
    }
  }, [timerPaused])

  useInterval(() => {
    // Once the time-ended alert is up this section is done; do not keep
    // decrementing. On a section transition the reconciliation effect re-derives
    // `currentAssignmentTime` to the NEXT section's full time, and this guard
    // ensures that re-derived value is frozen (never counted down) during the
    // modal/navigation gap, so it is not burned before the next section starts.
    if (
      !timerPaused &&
      currentAssignmentTime !== null &&
      !timerFrozen &&
      !isSectionTimerHandoff(utaId)
    ) {
      if (currentAssignmentTime <= 0) {
        setAutoSubmitpopUp(true)
      } else {
        setCurrentAssignmentTime((oldTime) => oldTime - TIMER_INTERVAL)
      }
    }
  }, TIMER_INTERVAL)

  useEffect(() => {
    if (autoSubmitPopUp) {
      const pauseVQVideoOnTestTimeOut = new Event('pauseVQVideoOnTestTimeOut')
      document?.dispatchEvent(pauseVQVideoOnTestTimeOut)
    }
  }, [autoSubmitPopUp])

  useInterval(() => {
    // Freeze sync once the time-ended alert is up: after a section expires the
    // server resets this doc to the next section, and the reconciliation effect
    // re-derives `currentAssignmentTime` to that next section's full time. We
    // must NOT write that value back as the next section's `timeSpent`, or it
    // would be consumed during the inter-section gap (ASM-405 "gap burn").
    // Same for manual section submit handoff.
    if (timerFrozen || isSectionTimerHandoff(utaId)) {
      return
    }
    if (docRef && utaId && currentAssignmentTime && currentAssignmentTime > 0) {
      if (docRef.current) {
        docRef.current.get().then((snapshot) => {
          const { timeSpent = 0 } = snapshot.data()
          const _syncOffset = uta.allowedTime - currentAssignmentTime || 0
          docRef.current.update({
            lastResumed: firebase.firestore.FieldValue.serverTimestamp(),
            timeSpent: Math.max(timeSpent, _syncOffset),
          })
        })
      } else {
        Sentry.captureException(
          new Error(
            `[Timed Assignment] Missing Doc Ref at time ${currentAssignmentTime}ms on uta ${utaId} and group ${groupId}`
          )
        )
      }
    } else if (currentAssignmentTime > 0 && !isAuthorPreview) {
      Sentry.captureException(
        new Error(
          `[Timed Assignment] Unable to Sync time ${currentAssignmentTime} on uta ${utaId} and group ${groupId}`
        )
      )
    }
  }, SYNC_INTERVAL)

  return (
    <>
      {(uta || isAuthorPreview) && (
        <TimerWrapper
          isDanger={currentAssignmentTime <= CAUTION_TIME}
          fgColor={fgColor}
          bgColor={bgColor}
          style={style}
        >
          <Icon type="clock-circle" />
          <Label
            isDanger={currentAssignmentTime <= CAUTION_TIME}
            fgColor={fgColor}
          >
            {getFormattedTime(
              currentAssignmentTime < 0 ? 0 : currentAssignmentTime
            )}
          </Label>
        </TimerWrapper>
      )}
      {autoSubmitPopUp && (uta || isAuthorPreview) && (
        <AssignmentTimeEndedAlert
          isVisible={autoSubmitPopUp}
          groupId={groupId}
          utaId={utaId}
          isAuthorPreview={isAuthorPreview}
        />
      )}
    </>
  )
}

const enhance = compose(
  withRouter,
  connect(
    (state) => ({
      updateUtaTimeType: state.studentAssignment.updateUtaTimeType,
      isPasswordValidated: state.test.isPasswordValidated,
      isPreview: state.test.isTestPreviewModalVisible,
      userRole: getUserRole(state),
      allowedTime: state.test.settings.allowedTime,
      isSectionAdaptiveTest: isSectionAdaptiveTestSelector(state),
    }),
    {
      resetUpdateUtaType: utaStartTimeUpdateRequired,
    }
  )
)

export default enhance(TimedTestTimer)

const TimerWrapper = styled.div`
  display: flex;
  align-items: center;
  font-size: 16px;
  min-height: 40px;
  margin-left: 16px;
  border-radius: 5px;
  padding: ${({ bgColor }) => bgColor && '2px 10px'};
  background: ${({ bgColor }) => bgColor};

  i {
    color: ${({ fgColor, isDanger }) => (isDanger ? red : fgColor || white)};
    transform: scale(1.5);
  }
`

const Label = styled.label`
  font-weight: 600;
  color: ${({ fgColor, isDanger }) => (isDanger ? red : fgColor || white)};
  margin-left: 10px;
`
